import { Marked } from "marked";
import { Parser } from "htmlparser2";

/** Shared deterministic boundary: imported by the edge and browser; no environment access. */
export { MAX_DICTATION_BYTES, MAX_DICTATION_SECONDS } from "./speech-limits.ts";
export const MAX_SPEECH_CHARACTERS = 5_000;
export const MAX_SPEECH_AUDIO_BYTES = 8 * 1024 * 1024;

const markdown = new Marked({ async: false, gfm: true });
const hiddenElements = new Set(["script", "style", "template", "head", "noscript"]);
const blockElements = new Set([
  "p",
  "div",
  "section",
  "article",
  "header",
  "footer",
  "aside",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "br",
  "hr",
  "li",
  "ul",
  "ol",
  "pre",
  "table",
  "tr",
  "td",
  "th",
  "dl",
  "dt",
  "dd",
]);

function hiddenAttributes(attributes: Record<string, string>): boolean {
  if (
    Object.hasOwn(attributes, "hidden") ||
    attributes["aria-hidden"]?.trim().toLowerCase() === "true"
  )
    return true;
  const style = (attributes.style ?? "").replace(/\/\*[\s\S]*?\*\//g, "");
  return style.split(";").some((declaration) => {
    const colon = declaration.indexOf(":");
    if (colon < 0) return false;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration
      .slice(colon + 1)
      .replace(/!\s*important\s*$/i, "")
      .trim()
      .toLowerCase();
    return (
      (property === "display" && value === "none") ||
      (property === "visibility" && ["hidden", "collapse"].includes(value))
    );
  });
}

// Markdown resolves emphasis, escaped/code literals and complete link destinations.
// The HTML parser then extracts visible text only; generated HTML never enters a DOM.
// This Xan-owned function is shared by browser admission and server billing.
export function readableAnswer(content: string): string {
  const pieces: string[] = [];
  const stack: { name: string; hidden: boolean }[] = [];
  let hiddenDepth = 0;
  const parser = new Parser(
    {
      onopentag(name, attributes) {
        const hidden = hiddenElements.has(name) || hiddenAttributes(attributes);
        if (!hiddenDepth && blockElements.has(name)) pieces.push(" ");
        stack.push({ name, hidden });
        if (hidden) hiddenDepth++;
      },
      ontext(text) {
        if (!hiddenDepth) pieces.push(text);
      },
      onclosetag(name) {
        const index = stack.map((element) => element.name).lastIndexOf(name);
        if (index !== -1) {
          for (const element of stack.splice(index)) if (element.hidden) hiddenDepth--;
        }
        if (!hiddenDepth && blockElements.has(name)) pieces.push(" ");
      },
    },
    { decodeEntities: true, lowerCaseTags: true, lowerCaseAttributeNames: true },
  );
  parser.end(markdown.parse(content, { async: false }));
  return pieces.join("").replace(/\s+/g, " ").trim();
}

export function speechTextReason(text: string, isFinal: boolean): string | null {
  if (!isFinal) return "Read aloud requires a completed answer.";
  const readable = readableAnswer(text);
  if (!readable) return "This answer has no readable text.";
  return Array.from(readable).length > MAX_SPEECH_CHARACTERS
    ? "Read aloud is limited to 5,000 characters. The answer will not be truncated."
    : null;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192)
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(binary);
}
export function base64ToBytes(value: string, limit: number): Uint8Array {
  if (!value || value.length > Math.ceil(limit / 3) * 4 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value))
    throw new Error("Invalid audio encoding");
  const decoded = atob(value);
  if (decoded.length > limit) throw new Error("Audio exceeds the size limit");
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}
export interface SpeechCapability {
  available: boolean;
  reason?: string;
  voice?: string;
  model?: string;
}
export interface SpeechCapabilities {
  approvalCapture: SpeechCapability;
  dictation: SpeechCapability;
  readAloud: SpeechCapability;
}
export const UNAVAILABLE_SPEECH: SpeechCapabilities = {
  approvalCapture: {
    available: false,
    reason:
      "Voice approval is unavailable until dedicated capture and provider access are verified.",
  },
  dictation: {
    available: false,
    reason: "Dictation is unavailable until provider access and usage limits are verified.",
  },
  readAloud: {
    available: false,
    reason: "Read aloud is unavailable until voice, model, privacy and usage limits are verified.",
  },
};
